import type { EnrollmentChoices } from 'bundles/enroll/components/xdp/withEnrollment';
import googleCertificateExperiments from 'bundles/epic/clients/GoogleCertificate';
import type CourseTypeMetadataV1 from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type OpenCourseMembershipsV1 from 'bundles/naptimejs/resources/openCourseMemberships.v1';

import _t from 'i18n!nls/enroll';

export const generateImpressionForFreemiumModelExperiment = ({
  isCourseraPlusLandingPage,
  courseId,
}: {
  isCourseraPlusLandingPage?: boolean;
  courseId?: string;
}) => {
  // Generate impression if on C+ landing page or passing in a courseId from XDP enroll modal
  if (!isCourseraPlusLandingPage && !courseId) {
    return;
  }

  // The C+ landing page doesn't have a courseId to check for exclusion with tag targeting,
  // so just passing in a fake value to make the Epic impression fire
  const courseIdToCheck = isCourseraPlusLandingPage ? 'cplus-landing-page' : courseId!;

  // eslint-disable-next-line camelcase
  googleCertificateExperiments.get('freemiumModelAaTest', { course_id: courseIdToCheck });

  // eslint-disable-next-line camelcase
  googleCertificateExperiments.get('isFreemiumModelEnabled', { course_id: courseIdToCheck });
};

export const isFreemiumModelEnabled = (courseId?: string) => {
  if (!courseId) {
    return false;
  }

  // eslint-disable-next-line camelcase
  return googleCertificateExperiments.preview('isFreemiumModelEnabled', { course_id: courseId });
};

export const isEnrolledThroughFreemiumAccess = (membership?: OpenCourseMembershipsV1) => {
  if (!membership) {
    return false;
  }

  // TODO add `preview` field check on membership after https://coursera.atlassian.net/browse/MONETIZE-1499 is done
  return isFreemiumModelEnabled(membership.courseId);
};

// This is intended for LIHP course card usage
export const useIsEnrolledThroughFreemiumAccess = (courseId?: string) => {
  // TODO implement OpenCourseMembership call through useNaptime to retrieve membership
  // return isEnrolledThroughFreemiumAccess(membership);

  // TODO remove after above is implemented
  return isFreemiumModelEnabled(courseId);
};

// TODO update description copies once they are finalized
export const getFreemiumEnrollModalCopy = () => ({
  auditOnly: _t('Preview this course'),
  auditOnlyDescription: _t('You will have access to all course materials except graded items'),
  fullCourseNoCertificate: _t('Preview this course'),
  fullCourseNoCertificateDescription: _t('You will still have access to all course materials for this course'),
});

export const canEnrollThroughFreemium = ({
  courseId,
  enrollmentAvailableChoices,
  courseTypeMetadataWithVersion,
}: {
  courseId: string;
  enrollmentAvailableChoices: EnrollmentChoices;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
}) => {
  if (!isFreemiumModelEnabled(courseId)) {
    return false;
  }

  const { canSubscribeToS12n, canPurchaseSingleCourse, canAuditCourse, hasFreeEnrollOptionIntoCourse } =
    enrollmentAvailableChoices;

  const canAuditAndSubscribeToS12n = canSubscribeToS12n && canAuditCourse;
  const canAuditAndPurchaseCourse = canPurchaseSingleCourse && canAuditCourse;
  const canFreeEnrollAndPurchaseCourse = canPurchaseSingleCourse && hasFreeEnrollOptionIntoCourse;
  const isStandardCourse = courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.STANDARD_COURSE;

  return (
    isStandardCourse && (canAuditAndSubscribeToS12n || canAuditAndPurchaseCourse || canFreeEnrollAndPurchaseCourse)
  );
};
